/**
 * This hook is used to fetch activity logs for an order.
 * It is used to display the activity logs in the order detail page.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OrderRepository } from 'src/api/order_repository';
import { ActivityLogQuery, ActivityLogFieldsFragment } from 'src/types';

export const useActivityLogs = (
  orderId: string,
  orderRepository: OrderRepository,
) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const {
    data: activityLogsData,
    isLoading,
    error,
  } = useQuery<ActivityLogQuery>({
    queryKey: ['activityLog', orderId],
    queryFn: async () => {
      return orderRepository.getActivityLogs(orderId);
    },
    enabled: !!isOpen,
  });

  const activityLogs: ActivityLogFieldsFragment[] =
    activityLogsData?.activityLog?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is ActivityLogFieldsFragment => node != null) || [];

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return {
    activityLogs,
    isLoading,
    error,
    // Dropdown state
    anchorEl,
    isOpen,
    handleOpen,
    handleClose,
  };
};
